import { useMemo, useState } from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { DateRangePicker, type DateRange } from "@/components/cinch/DatePicker";
import { DeltaCell } from "@/components/cinch/DeltaCell";
import {
  ActiveFilterPill,
  CheckboxList,
  FilterBar,
  FilterChip,
} from "@/components/cinch/FilterBar";
import { Matrix, type MatrixCell } from "@/components/cinch/Matrix";
import { RankDelta } from "@/components/cinch/RankDelta";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { TierChip } from "@/components/cinch/TierChip";
import { cn } from "@/lib/utils";

import {
  useArrLeaderboard,
  useCsmOptions,
  usePeakChurn,
  useTierMovement,
  type LeaderboardResponse,
} from "../api";
import { type CustomerTiersSearch } from "..";
import { AccountDrawer } from "../components/AccountDrawer";
import { TierRetentionDrawer, type RetentionSegment } from "../components/TierRetentionDrawer";
import {
  monthLabel,
  useRetentionMatrix,
  type KpiMonthRange,
  type KpiRetentionMatrix,
  type RetentionMatrixRow,
} from "../../kpi/api";
import { fmtCur, fmtPct } from "../../renewals/format";

type Tab = "movement" | "peak-churn" | "leaderboard" | "retention";

const toYm = (d: Date | null): string | null =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` : null;

function firstOfMonth(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function defaultRange(): { start: string; end: string; range: DateRange } {
  // Previous three full months (e.g. in July: Apr–Jun).
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const start = new Date(end.getFullYear(), end.getMonth() - 2, 1);
  return {
    start: firstOfMonth(start),
    end: firstOfMonth(end),
    range: { start, end },
  };
}

const TABS: { key: Tab; label: string; description: string }[] = [
  { key: "movement", label: "Tier movement", description: "Where did each customer start vs end in the window?" },
  { key: "peak-churn", label: "Peak-tier churn", description: "For each tier, what fraction of accounts that reached it have since churned?" },
  { key: "leaderboard", label: "ARR leaderboard", description: "Beginning vs ending ARR, ranked." },
  { key: "retention", label: "Retention", description: "NRR & GDR by tier band, as of the range's end month. Click a row to drill in." },
];

export function CustomerTiersPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/customer-tiers" }) as CustomerTiersSearch;
  const initial = defaultRange();
  const [range, setRange] = useState<DateRange>(initial.range);
  const [includeMailers, setIncludeMailers] = useState(false);
  // CSM filter token (owner:… / user:… / partner:…) from the renewals CSM data;
  // null = all CSMs. Doesn't apply to the entity-rolled Retention tab.
  const [csm, setCsm] = useState<string | null>(null);
  const csmOptions = useCsmOptions();
  const csmComboOptions = useMemo<ComboOption[]>(
    () =>
      (csmOptions.data?.options ?? []).map((o) => ({
        id: o.value,
        label: o.label,
        group: o.kind === "partner" ? "Managing partners" : "CSMs",
        badge: String(o.account_count),
      })),
    [csmOptions.data],
  );
  const selectedCsmLabel = useMemo(
    () => csmComboOptions.find((o) => o.id === csm)?.label,
    [csmComboOptions, csm],
  );
  // Tab lives in the URL (?tab=…) — a plain push (no `replace`) so the browser
  // back button steps back to the tab you were previously on.
  const tab: Tab = search.tab ?? "movement";
  const setTab = (next: Tab) =>
    navigate({
      to: "/customer-tiers",
      search: (prev: CustomerTiersSearch) => ({ ...prev, tab: next }),
    });

  const params = useMemo(() => {
    if (!range.start || !range.end) return null;
    return {
      start: firstOfMonth(range.start),
      end: firstOfMonth(range.end),
      include_mailers: includeMailers,
      csm: csm ?? undefined,
    };
  }, [range, includeMailers, csm]);

  const movement = useTierMovement(params ?? { start: "", end: "" });
  const peak = usePeakChurn(params ?? { start: "", end: "" });
  const leaderboard = useArrLeaderboard(params ?? { start: "", end: "" });

  // Retention (NRR/GDR) is a YoY point-in-time read, so it anchors to the end
  // month of the selected range. `include_mailers` doesn't apply — the KPI
  // retention dataset is entity-rolled off the revenue waterfall.
  const ymRange = useMemo<KpiMonthRange>(
    () => ({ start: toYm(range.start), end: toYm(range.end) }),
    [range],
  );
  const retention = useRetentionMatrix("CUSTOM", ymRange, "base", tab === "retention");
  const [retSegment, setRetSegment] = useState<RetentionSegment | null>(null);

  const active = TABS.find((t) => t.key === tab)!;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Customer Tiers</h1>
        <p className="text-sm text-muted-foreground">
          Movement, peak-tier churn, ARR leaderboard, and NRR/GDR retention across the selected month range.
        </p>
      </div>

      <FilterBar className="items-end">
        <div>
          <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Range</div>
          <DateRangePicker value={range} onChange={setRange} />
        </div>
        {/* CSM + mailer filters run off the report datasets, so they don't
            apply to the entity-rolled Retention tab — hide them there.
            The CSM filter uses the renewals CSM assignments (incl. managing
            partners), surfaced via the DS FilterChip + Combobox. */}
        {tab !== "retention" && (
          <>
            <FilterChip label="CSM" valueLabel={selectedCsmLabel}>
              <Combobox
                options={csmComboOptions}
                value={csm}
                onChange={setCsm}
                placeholder="All CSMs"
                searchPlaceholder="Search CSMs…"
                emptyText={csmOptions.isLoading ? "Loading…" : "No CSMs found."}
              />
            </FilterChip>
            <FilterChip label="Mailer revenue" count={includeMailers ? 1 : 0}>
              <CheckboxList
                options={[{ value: "mailer", label: "Include mailer revenue" }]}
                selected={includeMailers ? ["mailer"] : []}
                onChange={(v) => setIncludeMailers(v.includes("mailer"))}
              />
            </FilterChip>
          </>
        )}
      </FilterBar>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium",
              tab === t.key
                ? "border-brand text-brand"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{active.label}</CardTitle>
          <CardDescription>{active.description}</CardDescription>
        </CardHeader>
        <CardContent>
          {tab === "movement" && (
            <MovementTab data={movement.data} loading={movement.isLoading} error={movement.error} />
          )}
          {tab === "peak-churn" && (
            <PeakChurnTab data={peak.data} loading={peak.isLoading} error={peak.error} />
          )}
          {tab === "leaderboard" && (
            <LeaderboardTab data={leaderboard.data} loading={leaderboard.isLoading} error={leaderboard.error} />
          )}
          {tab === "retention" && (
            <RetentionTab
              data={retention.data}
              loading={retention.isLoading}
              error={retention.error}
              onOpen={setRetSegment}
            />
          )}
        </CardContent>
      </Card>

      <AccountDrawer />
      <TierRetentionDrawer
        segment={retSegment}
        range={ymRange}
        onClose={() => setRetSegment(null)}
      />
    </div>
  );
}

function StatePanel({ loading, error }: { loading: boolean; error: unknown }) {
  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (error) return <p className="text-sm text-destructive">{String(error)}</p>;
  return null;
}

// ── Shared account-drawer navigation ─────────────────────────────────
// Every table that surfaces an account behaves like the ARR leaderboard: a row
// click opens the quick view (?account); clicking the account name opens the
// expanded view (?account&expanded). `tab` is preserved so closing the drawer
// returns you to the tab you were on.

function useAccountNav() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/customer-tiers" }) as CustomerTiersSearch;
  const openAccount = (rawId: string | number, expanded: boolean) => {
    const account = Number(rawId);
    if (!Number.isFinite(account)) return;
    navigate({
      to: "/customer-tiers",
      search: (prev: CustomerTiersSearch) => ({
        ...prev,
        account,
        expanded: expanded ? true : undefined,
      }),
    });
  };
  return { openAccount, selectedId: search.account ?? null };
}

/** Account name (opens expanded view) + muted QB id. Stops row-click propagation. */
function AccountCell({
  id,
  name,
  onExpand,
}: {
  id: string | number;
  name: string;
  onExpand: () => void;
}) {
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onExpand();
        }}
        className="text-left font-medium hover:text-brand hover:underline focus-visible:text-brand focus-visible:outline-none"
        title="Open full account view"
      >
        {name}
      </button>{" "}
      <span className="text-xs text-muted-foreground">QB-{id}</span>
    </>
  );
}

/** Tier movement → leaderboard-style color cue: up is good (green), held is ok
 *  (yellow), down is bad (red). */
function movementVariant(m: "UPGRADED" | "DOWNGRADED" | "STABLE"): "success" | "warning" | "danger" {
  if (m === "UPGRADED") return "success";
  if (m === "DOWNGRADED") return "danger";
  return "warning";
}

const MOVEMENT_LABEL: Record<"UPGRADED" | "DOWNGRADED" | "STABLE", string> = {
  UPGRADED: "Upgraded",
  DOWNGRADED: "Downgraded",
  STABLE: "Stable",
};

function MovementTab({
  data,
  loading,
  error,
}: {
  data?: import("../api").MovementResponse;
  loading: boolean;
  error: unknown;
}) {
  const { openAccount, selectedId } = useAccountNav();
  // Clicking a matrix cell filters the table below to that exact transition
  // (start tier → end tier). Clicking the active cell again clears it.
  const [cellFilter, setCellFilter] = useState<{ start: number; end: number } | null>(null);

  if (!data) return <StatePanel loading={loading} error={error} />;

  // Tone the matrix: diagonal = held tier (brand), above = downgrade (danger),
  // below = upgrade (success), zero = no accounts (muted). Populated cells are
  // clickable — they drive the filter for the table below.
  const tiers = [1, 2, 3, 4, 5];
  const matrixCells: MatrixCell[][] = tiers.map((i) =>
    tiers.map((j) => {
      const v = data.matrix[`${i}_${j}`] ?? 0;
      const tone: MatrixCell["tone"] =
        v === 0 ? "neutral" : i === j ? "brand" : i < j ? "danger" : "success";
      const isSelected = cellFilter != null && cellFilter.start === i && cellFilter.end === j;
      return {
        value: v,
        tone,
        title: `T${i} → T${j}: ${v}`,
        selected: isSelected,
        onClick:
          v > 0
            ? () =>
                setCellFilter((cur) =>
                  cur && cur.start === i && cur.end === j ? null : { start: i, end: j },
                )
            : undefined,
      };
    }),
  );

  const filteredRows = cellFilter
    ? data.rows.filter(
        (r) => r.tier_at_start === cellFilter.start && r.tier_at_end === cellFilter.end,
      )
    : data.rows;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Kpi label="In window" value={String(data.kpis.in_window)} />
        <Kpi label="Upgraded" value={String(data.kpis.upgraded)} hint={fmtCur(data.kpis.upgraded_mrr_delta)} />
        <Kpi label="Stable" value={String(data.kpis.stable)} hint={fmtCur(data.kpis.stable_mrr_delta)} />
        <Kpi label="Downgraded" value={String(data.kpis.downgraded)} hint={fmtCur(data.kpis.downgraded_mrr_delta)} />
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold">Tier-to-tier transition matrix</h3>
        <Matrix
          cornerLabel="from → to"
          columns={tiers.map((j) => (
            <span key={j} className="inline-flex items-center gap-1">
              <span aria-hidden>→</span>
              <TierChip tier={j} />
            </span>
          ))}
          rows={tiers.map((i) => (
            <span key={i} className="inline-flex items-center gap-1">
              <TierChip tier={i} />
              <span aria-hidden>→</span>
            </span>
          ))}
          cells={matrixCells}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold">Accounts</h3>
          {cellFilter ? (
            <>
              <span className="text-xs text-muted-foreground">filtered to</span>
              <span className="inline-flex items-center gap-1">
                <TierChip tier={cellFilter.start} />
                <span aria-hidden className="text-muted-foreground">→</span>
                <TierChip tier={cellFilter.end} />
              </span>
              <ActiveFilterPill
                label={`${filteredRows.length} account${filteredRows.length === 1 ? "" : "s"}`}
                onRemove={() => setCellFilter(null)}
              />
            </>
          ) : (
            <span className="text-xs text-muted-foreground">
              click a matrix cell to filter — {data.rows.length} total
            </span>
          )}
        </div>

        <Table containerClassName="max-h-[calc(100vh-24rem)]">
          <TableHeader>
            <TableRow>
              <TableHead>Account</TableHead>
              <TableHead>Start period</TableHead>
              <TableHead>End period</TableHead>
              <TableHead>Tier movement</TableHead>
              <TableHead align="right">MRR change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody zebra>
            {filteredRows.length === 0 ? (
              <TableEmpty colSpan={5}>No accounts match this transition.</TableEmpty>
            ) : (
              filteredRows.map((r) => (
                <TableRow
                  key={String(r.counterparty_id)}
                  clickable
                  selected={selectedId != null && Number(r.counterparty_id) === selectedId}
                  onClick={() => openAccount(r.counterparty_id, false)}
                >
                  <TableCell>
                    <AccountCell
                      id={r.counterparty_id}
                      name={r.counterparty_name}
                      onExpand={() => openAccount(r.counterparty_id, true)}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.first_period}</TableCell>
                  <TableCell className="font-mono text-xs">{r.last_period}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <TierChip tier={r.tier_at_start} />
                      <span aria-hidden className="text-muted-foreground">→</span>
                      <TierChip tier={r.tier_at_end} />
                      <Badge variant={movementVariant(r.movement)}>{MOVEMENT_LABEL[r.movement]}</Badge>
                    </div>
                  </TableCell>
                  <TableCell align="right">
                    <DeltaCell value={r.mrr_at_end - r.mrr_at_start} format={fmtCur} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function PeakChurnTab({
  data,
  loading,
  error,
}: {
  data?: import("../api").PeakChurnResponse;
  loading: boolean;
  error: unknown;
}) {
  const { openAccount, selectedId } = useAccountNav();

  if (!data) return <StatePanel loading={loading} error={error} />;

  // Roll-ups for the footer. Churn rate is the blended rate (total churned ÷
  // total reached), not a sum of the per-tier rates.
  const totalReached = data.summary.reduce((acc, s) => acc + s.reached, 0);
  const totalChurned = data.summary.reduce((acc, s) => acc + s.churned, 0);
  const totalMrrLost = data.summary.reduce((acc, s) => acc + s.peak_mrr_lost, 0);
  const blendedChurnRate = totalReached > 0 ? (totalChurned / totalReached) * 100 : 0;

  return (
    <div className="flex flex-col gap-6">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Peak tier</TableHead>
            <TableHead align="right">Reached</TableHead>
            <TableHead align="right">Churned</TableHead>
            <TableHead align="right">Churn rate</TableHead>
            <TableHead align="right">Peak MRR lost</TableHead>
            <TableHead align="right">Churned revenue (ARR)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody zebra>
          {data.summary.map((s) => (
            <TableRow key={s.peak_tier}>
              <TableCell>
                <TierChip tier={s.peak_tier} />
              </TableCell>
              <TableCell numeric>{s.reached}</TableCell>
              <TableCell numeric>{s.churned}</TableCell>
              <TableCell numeric>{fmtPct(s.churn_rate_pct)}</TableCell>
              <TableCell numeric>{fmtCur(s.peak_mrr_lost)}</TableCell>
              <TableCell numeric>{fmtCur(s.peak_mrr_lost * 12)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell className="text-xs font-semibold uppercase tracking-wider">Total</TableCell>
            <TableCell numeric className="font-semibold">{totalReached}</TableCell>
            <TableCell numeric className="font-semibold">{totalChurned}</TableCell>
            <TableCell numeric className="font-semibold">{fmtPct(blendedChurnRate)}</TableCell>
            <TableCell numeric className="font-semibold">{fmtCur(totalMrrLost)}</TableCell>
            <TableCell numeric className="font-semibold">{fmtCur(totalMrrLost * 12)}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      <div>
        <h3 className="mb-2 text-sm font-semibold">Churned (sorted by peak MRR)</h3>
        <Table containerClassName="max-h-[calc(100vh-24rem)]">
          <TableHeader>
            <TableRow>
              <TableHead>Account</TableHead>
              <TableHead>Peak tier</TableHead>
              <TableHead>Peak period</TableHead>
              <TableHead align="right">Peak MRR</TableHead>
              <TableHead align="right">Peak ARR</TableHead>
              <TableHead>Last active</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody zebra>
            {data.churned.map((row, i) => {
              const r = row as Record<string, unknown>;
              const id = r.counterparty_id as string | number;
              const peakTier = Number(r.peak_tier);
              return (
                <TableRow
                  key={i}
                  clickable
                  selected={selectedId != null && Number(id) === selectedId}
                  onClick={() => openAccount(id, false)}
                >
                  <TableCell>
                    <AccountCell
                      id={id}
                      name={String(r.counterparty_name)}
                      onExpand={() => openAccount(id, true)}
                    />
                  </TableCell>
                  <TableCell>
                    <TierChip tier={Number.isFinite(peakTier) ? peakTier : null} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{String(r.peak_period)}</TableCell>
                  <TableCell numeric>{fmtCur(Number(r.peak_mrr))}</TableCell>
                  <TableCell numeric>{fmtCur(Number(r.peak_mrr) * 12)}</TableCell>
                  <TableCell className="font-mono text-xs">{String(r.last_active_period)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── ARR-leaderboard delta presentation ──────────────────────────────
// Δ ARR and Δ % render as chips: green when up, red when down, neutral when
// the account held flat (a "maintained" continuing customer).

function deltaVariant(n: number | null | undefined): "success" | "danger" | "neutral" {
  if (n == null || n === 0) return "neutral";
  return n > 0 ? "success" : "danger";
}

/** Currency with an explicit +/- sign; "—" when flat or unknown. */
function signedCur(n: number | null | undefined): string {
  if (n == null || n === 0) return "—";
  return (n > 0 ? "+" : "-") + fmtCur(Math.abs(n));
}

/** The API sends Δ% as a fraction (0.25 = 25%). Sign it; "—" when flat/unknown. */
function signedPct(n: number | null | undefined): string {
  if (n == null) return "—";
  const v = n * 100;
  if (v === 0) return "—";
  return (v > 0 ? "+" : "-") + Math.abs(v).toFixed(1) + "%";
}

/**
 * State chip. Continuing accounts stay silent — except when ARR is shrinking,
 * which we surface with at-risk colors. New logos pop (solid), churned and
 * merged route through the shared StatusBadge mapping.
 */
function LifecycleCell({
  status,
  arrDelta,
}: {
  status: LeaderboardResponse["rows"][number]["lifecycle_status"];
  arrDelta: number;
}) {
  switch (status) {
    case "NEW":
      return <StatusBadge value="new" />;
    case "CHURNED":
      return <StatusBadge value="churned" />;
    case "MERGED":
      return <StatusBadge value="merged" />;
    case "CONTINUING":
      return arrDelta < 0 ? <StatusBadge value="decreasing" /> : null;
    default:
      return null;
  }
}

function LeaderboardTab({
  data,
  loading,
  error,
}: {
  data?: LeaderboardResponse;
  loading: boolean;
  error: unknown;
}) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/customer-tiers" }) as CustomerTiersSearch;

  // Row → quick view (?account); the account name → expanded view
  // (?account&expanded). Plain push (no `replace`) so the back button closes
  // the drawer; `tab` is preserved so closing returns to the leaderboard.
  const openAccount = (rawId: string | number, expanded: boolean) => {
    const account = Number(rawId);
    if (!Number.isFinite(account)) return;
    navigate({
      to: "/customer-tiers",
      search: (prev: CustomerTiersSearch) => ({
        ...prev,
        account,
        expanded: expanded ? true : undefined,
      }),
    });
  };

  if (!data) return <StatePanel loading={loading} error={error} />;
  // When filtered to a CSM's book, surface the within-book rank alongside the
  // company-wide rank so both are visible (per product ask).
  const filtered = !!data.meta.csm;
  const colCount = filtered ? 9 : 8;
  return (
    <div className="overflow-hidden rounded-md border">
      <Table containerClassName="max-h-[calc(100vh-20rem)]">
        <TableHeader>
          <TableRow>
            <TableHead title="Rank across all customers">#</TableHead>
            {filtered && <TableHead title="Rank within this CSM's book">Book #</TableHead>}
            <TableHead>Account</TableHead>
            <TableHead align="right">Beginning ARR</TableHead>
            <TableHead align="right">Ending ARR</TableHead>
            <TableHead align="right">Δ ARR</TableHead>
            <TableHead align="right">Δ %</TableHead>
            <TableHead align="right">Rank change</TableHead>
            <TableHead>State</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody zebra>
          {data.rows.length === 0 ? (
            <TableEmpty colSpan={colCount}>No accounts in this range.</TableEmpty>
          ) : (
            data.rows.map((r, i) => (
              <TableRow
                key={String(r.counterparty_id) + i}
                clickable
                selected={search.account != null && Number(r.counterparty_id) === search.account}
                onClick={() => openAccount(r.counterparty_id, false)}
              >
                <TableCell>{r.ending_rank ?? "—"}</TableCell>
                {filtered && (
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="tabular-nums">{r.ending_rank_in_book ?? "—"}</span>
                      <RankDelta value={r.rank_change_in_book} />
                    </div>
                  </TableCell>
                )}
                <TableCell>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openAccount(r.counterparty_id, true);
                    }}
                    className="text-left font-medium hover:text-brand hover:underline focus-visible:text-brand focus-visible:outline-none"
                    title="Open full account view"
                  >
                    {r.counterparty_name}
                  </button>{" "}
                  <span className="text-xs text-muted-foreground">QB-{r.counterparty_id}</span>
                </TableCell>
                <TableCell numeric>{fmtCur(r.beginning_arr)}</TableCell>
                <TableCell numeric>{fmtCur(r.ending_arr)}</TableCell>
                <TableCell align="right">
                  <Badge variant={deltaVariant(r.arr_delta)} className="tabular-nums">
                    {signedCur(r.arr_delta)}
                  </Badge>
                </TableCell>
                <TableCell align="right">
                  <Badge variant={deltaVariant(r.arr_delta_pct)} className="tabular-nums">
                    {signedPct(r.arr_delta_pct)}
                  </Badge>
                </TableCell>
                <TableCell align="right">
                  <RankDelta value={r.rank_change} />
                </TableCell>
                <TableCell>
                  <LifecycleCell status={r.lifecycle_status} arrDelta={r.arr_delta} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Retention (NRR / GDR by tier band) ──────────────────────────────
// Mirrors the $ Retention KPI card's thresholds: ≥100% retained/expanding
// (green), 85–99% a soft loss (purple), below 85% a real loss (red).
function retentionTone(pct: number | null): string {
  if (pct == null) return "text-muted-foreground";
  if (pct >= 100) return "text-success";
  if (pct >= 85) return "text-purple";
  return "text-danger";
}

function PctCell({ value }: { value: number | null }) {
  return (
    <span className={cn("font-semibold tabular-nums", retentionTone(value))}>
      {value != null ? `${value.toFixed(0)}%` : "—"}
    </span>
  );
}

function RetentionTab({
  data,
  loading,
  error,
  onOpen,
}: {
  data?: KpiRetentionMatrix;
  loading: boolean;
  error: unknown;
  onOpen: (segment: RetentionSegment) => void;
}) {
  if (!data) return <StatePanel loading={loading} error={error} />;
  if (!data.available || !data.rows?.length) {
    return <p className="text-sm text-muted-foreground">No retention data for this range.</p>;
  }

  // A "top-N" band (1,2 / 1,2,3 / …) or All gets a divider above it so the
  // single-tier rows read as one group and the roll-ups as another.
  const isRollup = (r: RetentionMatrixRow) => r.tiers == null || r.tiers.length > 1;
  const firstRollupKey = data.rows.find(isRollup)?.key;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        Year-over-year retention as of{" "}
        <span className="font-medium text-foreground">{monthLabel(data.anchor_month)}</span>{" "}
        vs 12 months prior · banded by tier 12 months ago · entity-rolled. Click a band to
        drill into its accounts.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tier band</TableHead>
            <TableHead align="right">NRR</TableHead>
            <TableHead align="right">GDR</TableHead>
            <TableHead align="right">Entities</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.rows.map((r) => (
            <TableRow
              key={r.key}
              clickable
              onClick={() => onOpen({ key: r.key, label: r.label, tiers: r.tiers ?? [] })}
              className={cn(r.key === firstRollupKey && "border-t-2 border-border")}
            >
              <TableCell className="font-medium">{r.label}</TableCell>
              <TableCell align="right">
                <PctCell value={r.nrr_pct} />
              </TableCell>
              <TableCell align="right">
                <PctCell value={r.gdr_pct} />
              </TableCell>
              <TableCell numeric className="text-muted-foreground">
                {r.base_count}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription className="text-xs uppercase tracking-wider">{label}</CardDescription>
        <CardTitle className="text-3xl font-bold">{value}</CardTitle>
      </CardHeader>
      {hint && (
        <CardContent className="text-xs text-muted-foreground">{hint}</CardContent>
      )}
    </Card>
  );
}
